import type { SortDescriptor } from "react-aria-components";

import {
	AlertTriangle,
	ChevronDown,
	ChevronRight,
	Edit01,
	Eye,
	LayersTwo01,
	Map01,
	Map02,
	Modem02,
	Save01,
} from "@untitledui/icons";
import {
	Fragment,
	type KeyboardEvent,
	type MouseEvent,
	useEffect,
	useMemo,
	useState,
} from "react";

import { Table, TableCard } from "@/components/table/table";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { SearchInputWithClear } from "@/components/ui/input/search-input-with-clear";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import type {
	ShadowFloor,
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";

import {
	type RowActionItem,
	RowActionsMenu,
} from "../../components/row-actions-menu";
import {
	aggregateSensorStatusCounts,
	buildClusterStatusSegments,
} from "../../map/markers/aggregate-status";
import { formatLocationAddress } from "../../utils/location/format-location-address";
import { getRefId } from "../../utils/location/installation-ids";
import { isLocationMappable } from "../../utils/location/location-map-features";
import { sortFloorsForList } from "../../utils/location/location-map-utils";
import {
	formatLocationSetupGapsTooltip,
	getLocationSetupGaps,
	hasStructuralSetupGaps,
	type LocationSetupGap,
} from "../../utils/location/location-setup-gaps";
import { sensorStatusConfig } from "../../utils/sensor/sensor-status";
import { getSensorTypeIcon } from "../../utils/sensor/sensor-type";
import { isValidCoordinatePair } from "../../utils/shared/coordinates";
import {
	resolveLocationMapFocus,
	toLocationMapFocusEntities,
} from "../../utils/shared/location-map-focus-search-params";
import { toTitleCase } from "../../utils/shared/strings";

const VIEW_ON_MAP_DISABLED_DESCRIPTION = "No map coordinates for this item";

const isZoneMappable = (zone: ShadowZone): boolean => {
	if (zone.boundary) return true;
	return (zone.sensors ?? []).some((sensor) =>
		isValidCoordinatePair(sensor.geolocation?.coordinates),
	);
};

/** Independent pagination toggles for each nested list level. Omitted keys default to true. */
type ListPaginationConfig = {
	locations?: boolean;
	sensors?: boolean;
	zones?: boolean;
};

const DEFAULT_LIST_PAGINATION = {
	locations: true,
	sensors: true,
	zones: true,
} as const satisfies Required<ListPaginationConfig>;

const resolveListPagination = (
	config?: ListPaginationConfig,
): Required<ListPaginationConfig> => ({
	locations: config?.locations ?? DEFAULT_LIST_PAGINATION.locations,
	sensors: config?.sensors ?? DEFAULT_LIST_PAGINATION.sensors,
	zones: config?.zones ?? DEFAULT_LIST_PAGINATION.zones,
});

type LocationListViewProps = {
	/**
	 * Floor to expand when present (from URL `floor`). Corrected to the
	 * focused zone's current floor when that relationship has changed.
	 */
	focusFloorId?: null | string;
	/**
	 * Sensor to highlight when present in an expanded zone (from URL `sensor`).
	 */
	focusSensorId?: null | string;
	/**
	 * Zone to expand when present (from URL `zone`). A sensor that moved
	 * zones follows the sensor instead of this id.
	 */
	focusZoneId?: null | string;
	/**
	 * Per-level client-side pagination. Each key is independent — e.g.
	 * `{ locations: false, zones: true, sensors: true }` paginates nested
	 * lists only. Omitted keys default to `true`.
	 */
	includePagination?: ListPaginationConfig;
	locationInstallationCounts?: ReadonlyMap<string, number>;
	locations: ShadowLocation[];
	onAddFloor?: (locationId: string) => void;
	onAddInstallation?: (locationId: string) => void;
	onAddSensor?: (locationId: string, zoneId: string) => void;
	onAddZone?: (locationId: string) => void;
	onEditFloor?: (locationId: string, floorId: string) => void;
	onEditLocation?: (locationId: string) => void;
	onEditSensor?: (locationId: string, sensorId: string) => void;
	onEditZone?: (locationId: string, zoneId: string) => void;
	onSearchQueryChange?: (value: string) => void;
	onSelectLocation?: (locationId: string) => void;
	onViewLocation?: (locationId: string) => void;
	/** Open map view focused on a location, optional zone, and/or sensor. */
	onViewOnMap?: (focus: {
		locationId: string;
		sensorId?: string;
		zoneId?: string;
	}) => void;
	searchQuery?: string;
	selectedLocationId?: null | string;
	/**
	 * `locations` — Location → Zones → Sensors (org-wide list).
	 * `zones` — Zones → Sensors for a single focused location (no location row).
	 */
	variant?: "locations" | "zones";
};

/** Sticky location column header (toolbar sits outside the scrollport). */
const LOCATION_HEADER_STICKY_TOP = "top-0";
/** Sticky zone column header below the location column header (`sm` = h-9). */
const ZONE_HEADER_STICKY_TOP = "top-10";

const LOCATION_COLUMN_COUNT = 8;
const ZONE_COLUMN_COUNT = 5;
const FLOOR_COLUMN_COUNT = 5;
/** Card shell shared by the floors table and the unassigned-zones table. */
const FRAMED_TABLE_SHELL_CLASS =
	"rounded-lg border border-secondary bg-primary";
/**
 * `border-collapse` ignores cell radius, so a sticky header's square
 * background paints over the card's top corners. Separate borders let the
 * header cells follow the shell radius while the header can still stick.
 */
const FRAMED_TABLE_CLASS =
	"border-separate border-spacing-0 [&>tbody>tr:last-child>td:first-child]:rounded-bl-[calc(0.5rem-1px)] [&>tbody>tr:last-child>td:last-child]:rounded-br-[calc(0.5rem-1px)]";
const FRAMED_HEADER_CLASS =
	"bg-transparent [&>tr>th:first-child]:rounded-tl-[calc(0.5rem-1px)] [&>tr>th:last-child]:rounded-tr-[calc(0.5rem-1px)]";

const zonesOnFloor = (zones: readonly ShadowZone[], floorId: string) =>
	zones.filter((zone) => getRefId(zone.floor) === floorId);

const zonesWithoutFloor = (
	zones: readonly ShadowZone[],
	floorIds: ReadonlySet<string>,
) =>
	zones.filter((zone) => {
		const floorId = getRefId(zone.floor);
		return !floorId || !floorIds.has(floorId);
	});
const LIST_PAGE_SIZE = 10;

const paginate = <T,>(items: T[], page: number) => {
	const total = items.length;
	const totalPages = Math.max(1, Math.ceil(total / LIST_PAGE_SIZE));
	const safePage = Math.min(Math.max(page, 1), totalPages);
	const start = (safePage - 1) * LIST_PAGE_SIZE;
	return {
		page: safePage,
		pageItems: items.slice(start, start + LIST_PAGE_SIZE),
		total,
		totalPages,
	};
};

/** Compact page list with ellipsis (first / siblings / last). */
const buildPageItems = (
	page: number,
	totalPages: number,
	siblingCount = 1,
): Array<"ellipsis" | number> => {
	const totalSlots = siblingCount * 2 + 5;
	if (totalSlots >= totalPages) {
		return Array.from({ length: totalPages }, (_, index) => index + 1);
	}

	const leftSibling = Math.max(page - siblingCount, 1);
	const rightSibling = Math.min(page + siblingCount, totalPages);
	const showLeftEllipsis = leftSibling > 2;
	const showRightEllipsis = rightSibling < totalPages - 1;

	if (!showLeftEllipsis && showRightEllipsis) {
		const leftCount = siblingCount * 2 + 3;
		return [
			...Array.from({ length: leftCount }, (_, index) => index + 1),
			"ellipsis",
			totalPages,
		];
	}

	if (showLeftEllipsis && !showRightEllipsis) {
		const rightCount = siblingCount * 2 + 3;
		return [
			1,
			"ellipsis",
			...Array.from(
				{ length: rightCount },
				(_, index) => totalPages - rightCount + 1 + index,
			),
		];
	}

	return [
		1,
		"ellipsis",
		...Array.from(
			{ length: rightSibling - leftSibling + 1 },
			(_, index) => leftSibling + index,
		),
		"ellipsis",
		totalPages,
	];
};

const ListPagination = ({
	className,
	onPageChange,
	page,
	total,
	totalPages,
}: {
	className?: string;
	onPageChange: (page: number) => void;
	page: number;
	total: number;
	totalPages: number;
}) => {
	if (total <= LIST_PAGE_SIZE) return null;

	const pageItems = buildPageItems(page, totalPages);

	return (
		<div
			className={cx(
				"flex shrink-0 items-center justify-between gap-3 border-t border-secondary bg-primary px-3 py-2",
				className,
			)}
			onClick={(event) => {
				event.stopPropagation();
			}}
			onKeyDown={(event) => {
				event.stopPropagation();
			}}
		>
			<Button
				color="secondary"
				isDisabled={page <= 1}
				onClick={() => onPageChange(page - 1)}
				size="sm"
			>
				Previous
			</Button>
			<div className="flex items-center justify-center gap-0.5">
				{pageItems.map((item, index) =>
					item === "ellipsis" ? (
						<span
							aria-hidden
							className="flex size-8 shrink-0 items-center justify-center text-sm text-tertiary"
							key={`ellipsis-${index}`}
						>
							&#8230;
						</span>
					) : (
						<button
							aria-current={item === page ? "page" : undefined}
							aria-label={`Page ${item}`}
							className={cx(
								"flex size-8 shrink-0 items-center justify-center rounded-md text-sm font-medium tabular-nums",
								item === page
									? "bg-primary_hover text-secondary ring-1 ring-secondary ring-inset"
									: "text-fg-quaternary hover:bg-primary_hover hover:text-secondary",
							)}
							key={item}
							onClick={() => onPageChange(item)}
							type="button"
						>
							{item}
						</button>
					),
				)}
			</div>
			<Button
				color="secondary"
				isDisabled={page >= totalPages}
				onClick={() => onPageChange(page + 1)}
				size="sm"
			>
				Next
			</Button>
		</div>
	);
};

const locationFloorCount = (location: ShadowLocation) =>
	location.floors?.length ?? 0;

const locationSensorCount = (location: ShadowLocation) =>
	(location.zones ?? []).reduce(
		(count, zone) => count + (zone.sensors?.length ?? 0),
		0,
	);

const locationSensors = (location: ShadowLocation) =>
	(location.zones ?? []).flatMap((zone) => zone.sensors ?? []);

/** Sort key for sensor-health rollup: lower = healthier. */
const sensorHealthSortKey = (sensors: Array<{ status: string }>): number => {
	const { offline, online, warning } = aggregateSensorStatusCounts(sensors);
	const total = offline + online + warning;
	if (total === 0) return -1;
	if (offline === 0 && warning === 0) return 0;
	if (online === 0 && warning === 0) return 1_000_000 + offline;
	return offline * 1_000 + warning * 10 + 1;
};

const SensorHealthStatus = ({
	missingSetup,
	sensors,
}: {
	missingSetup?: readonly LocationSetupGap[];
	sensors: Array<{ status: string }>;
}) => {
	const segments = buildClusterStatusSegments({
		...aggregateSensorStatusCounts(sensors),
		missingSetup,
		surface: "light",
	});
	const missingTooltip = hasStructuralSetupGaps(missingSetup ?? [])
		? formatLocationSetupGapsTooltip(missingSetup ?? [])
		: "";

	return (
		<span className="flex flex-wrap items-center gap-x-1.5 text-sm leading-tight font-medium">
			{missingTooltip ? (
				<Tooltip placement="top" title={missingTooltip}>
					<TooltipTrigger
						aria-label={missingTooltip}
						className="inline-flex shrink-0"
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						<AlertTriangle className="size-3.5 shrink-0 text-utility-warning-500" />
					</TooltipTrigger>
				</Tooltip>
			) : null}
			{segments.map((segment) => (
				<span className={segment.className} key={segment.kind}>
					{segment.text}
				</span>
			))}
		</span>
	);
};

const compareSortValues = (
	first: number | string,
	second: number | string,
	direction: SortDescriptor["direction"],
): number => {
	const cmp =
		typeof first === "number" && typeof second === "number"
			? first - second
			: String(first).localeCompare(String(second), undefined, {
					numeric: true,
					sensitivity: "base",
				});
	return direction === "descending" ? -cmp : cmp;
};

const ExpandToggle = ({
	controlsId,
	isExpanded,
	label,
	onToggle,
}: {
	controlsId?: string;
	isExpanded: boolean;
	label: string;
	onToggle: (event: KeyboardEvent | MouseEvent) => void;
}) => (
	<button
		aria-controls={controlsId}
		aria-expanded={isExpanded}
		aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
		className="inline-flex size-7 items-center justify-center rounded-md text-fg-quaternary hover:bg-primary_hover hover:text-fg-secondary"
		onClick={onToggle}
		type="button"
	>
		{isExpanded ? (
			<ChevronDown className="size-4" />
		) : (
			<ChevronRight className="size-4" />
		)}
	</button>
);

const NestEmptyState = ({
	actionLabel,
	className,
	message,
	onAction,
}: {
	actionLabel?: string;
	className?: string;
	message: string;
	onAction?: () => void;
}) => (
	<div
		className={cx(
			"flex flex-col items-start gap-2 rounded-md border border-dashed border-secondary bg-primary px-4 py-5",
			className,
		)}
	>
		<p className="text-sm text-tertiary">{message}</p>
		{onAction && actionLabel ? (
			<button
				className="text-sm font-semibold text-brand-secondary hover:text-brand-secondary_hover"
				onClick={(event) => {
					event.stopPropagation();
					onAction();
				}}
				type="button"
			>
				{actionLabel}
			</button>
		) : null}
	</div>
);

const zoneBoundaryBadge = (
	zone: ShadowZone,
): { color: "gray" | "success"; label: string } => {
	if (zone.boundaryKind) {
		return { color: "success", label: toTitleCase(zone.boundaryKind) };
	}
	if (zone.boundary) return { color: "success", label: "Set" };
	return { color: "gray", label: "None" };
};

const SensorDetailStrip = ({
	focusSensorId,
	includePagination = true,
	locationId,
	onAddSensor,
	onEditSensor,
	onViewOnMap,
	sensors,
	zoneId,
}: {
	focusSensorId?: null | string;
	includePagination?: boolean;
	locationId: string;
	onAddSensor?: (locationId: string, zoneId: string) => void;
	onEditSensor?: (locationId: string, sensorId: string) => void;
	onViewOnMap?: (focus: {
		locationId: string;
		sensorId?: string;
		zoneId?: string;
	}) => void;
	sensors: ShadowSensor[];
	zoneId: string;
}) => {
	const [sensorPage, setSensorPage] = useState(1);
	const focusedSensorIndex = focusSensorId
		? sensors.findIndex((sensor) => sensor.id === focusSensorId)
		: -1;

	// Jump to the page that contains the focused sensor (including first mount).
	const [prevFocusSensorId, setPrevFocusSensorId] = useState<
		null | string | undefined
	>(undefined);
	if (focusSensorId !== prevFocusSensorId) {
		setPrevFocusSensorId(focusSensorId);
		if (includePagination && focusedSensorIndex >= 0) {
			setSensorPage(Math.floor(focusedSensorIndex / LIST_PAGE_SIZE) + 1);
		}
	}

	const sensorPagination = useMemo(
		() => paginate(sensors, sensorPage),
		[sensorPage, sensors],
	);
	const visibleSensors = includePagination
		? sensorPagination.pageItems
		: sensors;

	useEffect(() => {
		if (!focusSensorId || focusedSensorIndex < 0) return;
		const frame = requestAnimationFrame(() => {
			document
				.getElementById(`list-sensor-${focusSensorId}`)
				?.scrollIntoView({ behavior: "smooth", block: "nearest" });
		});
		return () => cancelAnimationFrame(frame);
	}, [focusSensorId, focusedSensorIndex, sensorPagination.page]);

	if (sensors.length === 0) {
		return (
			<NestEmptyState
				actionLabel={onAddSensor ? "Add sensor" : undefined}
				message="No sensors in this zone."
				onAction={
					onAddSensor ? () => onAddSensor(locationId, zoneId) : undefined
				}
			/>
		);
	}

	return (
		<div className="overflow-hidden rounded-md border border-secondary bg-primary">
			<ul className="divide-y divide-secondary">
				{visibleSensors.map((sensor) => {
					const status = sensorStatusConfig(sensor.status);
					const mapped = isValidCoordinatePair(sensor.geolocation?.coordinates);
					const SensorTypeIcon = getSensorTypeIcon(String(sensor.type));
					const isFocused = focusSensorId === sensor.id;
					const menuItems = [
						onViewOnMap
							? {
									description: mapped
										? undefined
										: VIEW_ON_MAP_DISABLED_DESCRIPTION,
									icon: Map01,
									id: "view-sensor-on-map",
									isDisabled: !mapped,
									label: "View on map",
									onAction: () =>
										onViewOnMap({
											locationId,
											sensorId: sensor.id,
											zoneId,
										}),
								}
							: null,
						onEditSensor
							? {
									icon: Edit01,
									id: "edit-sensor",
									label: "Edit sensor",
									onAction: () => onEditSensor(locationId, sensor.id),
								}
							: null,
						onAddSensor
							? {
									icon: Modem02,
									id: "add-sensor",
									label: "Add sensor",
									onAction: () => onAddSensor(locationId, zoneId),
								}
							: null,
					].filter(Boolean) as RowActionItem[];

					return (
						<li
							aria-current={isFocused ? "true" : undefined}
							className={cx(
								"flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5",
								isFocused && "bg-brand-primary_alt",
							)}
							id={`list-sensor-${sensor.id}`}
							key={sensor.id}
						>
							<div className="min-w-0 flex-1">
								<p className="truncate text-sm font-medium text-primary">
									{toTitleCase(sensor.name)}
								</p>
								<p className="mt-0.5 flex items-center gap-1 truncate text-xs text-tertiary">
									<SensorTypeIcon
										aria-hidden
										className="size-3.5 shrink-0 text-fg-quaternary"
									/>
									<span className="truncate">
										{toTitleCase(String(sensor.type))}
									</span>
								</p>
							</div>
							<Badge color={status.color} size="sm" type="pill-color">
								{status.label}
							</Badge>
							<Badge
								color={mapped ? "success" : "gray"}
								size="sm"
								type="pill-color"
							>
								{mapped ? "Mapped" : "Unmapped"}
							</Badge>
							<RowActionsMenu
								ariaLabel={`Actions for ${toTitleCase(sensor.name)}`}
								items={menuItems}
							/>
						</li>
					);
				})}
			</ul>
			{includePagination ? (
				<ListPagination
					onPageChange={setSensorPage}
					page={sensorPagination.page}
					total={sensorPagination.total}
					totalPages={sensorPagination.totalPages}
				/>
			) : null}
		</div>
	);
};

const ZonesTable = ({
	expandedZoneIds,
	focusSensorId,
	focusZoneId,
	framed = false,
	hasInstallation,
	includePagination = true,
	includeSensorPagination = true,
	locationId,
	locationName,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onEditSensor,
	onEditZone,
	onToggleZone,
	onViewOnMap,
	/** Pin pager to the card footer; body scrolls between header and footer. */
	pinPagination = false,
	showLocationSummary = true,
	stickyTopClassName,
	zones,
}: {
	expandedZoneIds: ReadonlySet<string>;
	focusSensorId?: null | string;
	focusZoneId?: null | string;
	/** Draw the same rounded card as the floors table. */
	framed?: boolean;
	hasInstallation: boolean;
	includePagination?: boolean;
	includeSensorPagination?: boolean;
	locationId: string;
	locationName: string;
	onAddInstallation?: (locationId: string) => void;
	onAddSensor?: (locationId: string, zoneId: string) => void;
	onAddZone?: (locationId: string) => void;
	onEditSensor?: (locationId: string, sensorId: string) => void;
	onEditZone?: (locationId: string, zoneId: string) => void;
	onToggleZone: (zoneId: string, event: KeyboardEvent | MouseEvent) => void;
	onViewOnMap?: (focus: {
		locationId: string;
		sensorId?: string;
		zoneId?: string;
	}) => void;
	pinPagination?: boolean;
	showLocationSummary?: boolean;
	stickyTopClassName: string;
	zones: ShadowZone[];
}) => {
	const [zonePage, setZonePage] = useState(1);
	const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
		column: "zone-name",
		direction: "ascending",
	});

	const sortedZones = useMemo(() => {
		const column = String(sortDescriptor.column ?? "zone-name");
		return [...zones].sort((a, b) => {
			switch (column) {
				case "zone-sensors":
					return compareSortValues(
						a.sensors?.length ?? 0,
						b.sensors?.length ?? 0,
						sortDescriptor.direction,
					);
				case "zone-status":
					return compareSortValues(
						sensorHealthSortKey(a.sensors ?? []),
						sensorHealthSortKey(b.sensors ?? []),
						sortDescriptor.direction,
					);
				case "zone-name":
				default:
					return compareSortValues(
						a.name ?? "",
						b.name ?? "",
						sortDescriptor.direction,
					);
			}
		});
	}, [sortDescriptor, zones]);

	const focusedZoneIndex = focusZoneId
		? sortedZones.findIndex((zone) => zone.id === focusZoneId)
		: -1;
	const [prevFocusZoneId, setPrevFocusZoneId] = useState<
		null | string | undefined
	>(undefined);
	if (focusZoneId !== prevFocusZoneId) {
		setPrevFocusZoneId(focusZoneId);
		if (includePagination && focusedZoneIndex >= 0) {
			setZonePage(Math.floor(focusedZoneIndex / LIST_PAGE_SIZE) + 1);
		}
	}

	useEffect(() => {
		if (!focusZoneId || focusedZoneIndex < 0) return;
		const frame = requestAnimationFrame(() => {
			document
				.getElementById(`list-zone-${focusZoneId}`)
				?.scrollIntoView({ behavior: "smooth", block: "nearest" });
		});
		return () => cancelAnimationFrame(frame);
	}, [focusZoneId, focusedZoneIndex, zonePage]);

	const zonePagination = useMemo(
		() => paginate(sortedZones, zonePage),
		[sortedZones, zonePage],
	);
	const visibleZones = includePagination
		? zonePagination.pageItems
		: sortedZones;
	const showPager = includePagination;

	if (zones.length === 0) {
		if (!hasInstallation) {
			return (
				<NestEmptyState
					actionLabel={onAddInstallation ? "Add installation" : undefined}
					message="Add an installation before creating zones."
					onAction={
						onAddInstallation ? () => onAddInstallation(locationId) : undefined
					}
				/>
			);
		}
		return (
			<NestEmptyState
				actionLabel={onAddZone ? "Add zone" : undefined}
				message="No zones yet."
				onAction={onAddZone ? () => onAddZone(locationId) : undefined}
			/>
		);
	}

	const pager = showPager ? (
		<ListPagination
			onPageChange={setZonePage}
			page={zonePagination.page}
			total={zonePagination.total}
			totalPages={zonePagination.totalPages}
		/>
	) : null;

	const isFramed = showLocationSummary || framed;

	const table = (
		<Table
			aria-label={`Zones for ${toTitleCase(locationName)}`}
			className={cx(
				!showLocationSummary && "min-w-[36rem]",
				isFramed && FRAMED_TABLE_CLASS,
			)}
			onSortChange={setSortDescriptor}
			scrollMode="none"
			size="sm"
			sortDescriptor={sortDescriptor}
		>
			<Table.Header
				sticky={{
					className: cx(
						stickyTopClassName,
						"z-20",
						isFramed && FRAMED_HEADER_CLASS,
					),
				}}
			>
				<Table.Head className="w-5 px-2" id="zone-expand" />
				<Table.Head
					allowsSorting
					className="pl-2"
					id="zone-name"
					isRowHeader
					label="Zone"
				/>
				<Table.Head allowsSorting id="zone-sensors" label="Sensors" />
				<Table.Head allowsSorting id="zone-status" label="Status" />
				<Table.Head className="w-14" id="zone-actions" />
			</Table.Header>
			<Table.Body>
				{visibleZones.map((zone) => {
					const sensors = zone.sensors ?? [];
					const isExpanded = expandedZoneIds.has(zone.id);
					const isFocusedZone = focusZoneId === zone.id;
					const panelId = `zone-sensors-${zone.id}`;
					const boundary = zoneBoundaryBadge(zone);
					const zoneMappable = isZoneMappable(zone);
					const menuItems = [
						onViewOnMap
							? {
									description: zoneMappable
										? undefined
										: VIEW_ON_MAP_DISABLED_DESCRIPTION,
									icon: Map01,
									id: "view-zone-on-map",
									isDisabled: !zoneMappable,
									label: "View on map",
									onAction: () => onViewOnMap({ locationId, zoneId: zone.id }),
								}
							: null,
						onEditZone
							? {
									icon: Edit01,
									id: "edit-zone",
									label: "Edit zone",
									onAction: () => onEditZone(locationId, zone.id),
								}
							: null,
						onAddZone
							? {
									icon: Map02,
									id: "add-zone",
									label: "Add zone",
									onAction: () => onAddZone(locationId),
								}
							: null,
						onAddSensor
							? {
									icon: Modem02,
									id: "add-sensor",
									label: "Add sensor",
									onAction: () => onAddSensor(locationId, zone.id),
								}
							: null,
					].filter(Boolean) as RowActionItem[];

					return (
						<Fragment key={zone.id}>
							<Table.Row
								className={isFocusedZone ? "bg-brand-primary_alt" : undefined}
								highlightSelectedRow={false}
								id={`list-zone-${zone.id}`}
							>
								<Table.Cell className="w-5 px-2">
									<ExpandToggle
										controlsId={panelId}
										isExpanded={isExpanded}
										label={toTitleCase(zone.name)}
										onToggle={(event) => onToggleZone(zone.id, event)}
									/>
								</Table.Cell>
								<Table.Cell className="pl-2">
									{/* Color tooltip is a button trigger — keep it a sibling of the
									    expand control so we don't nest <button> inside <button>. */}
									<div className="flex w-full min-w-0 items-center gap-2">
										{zone.color ? (
											<Tooltip placement="top" title={zone.color}>
												<TooltipTrigger
													aria-label={`Zone color ${zone.color}`}
													className="inline-flex shrink-0"
												>
													<span
														aria-hidden
														className="size-3 rounded-full ring-1 ring-secondary ring-inset"
														style={{ backgroundColor: zone.color }}
													/>
												</TooltipTrigger>
											</Tooltip>
										) : null}
										<button
											aria-controls={panelId}
											aria-expanded={isExpanded}
											className="min-w-0 flex-1 text-left"
											onClick={(event) => onToggleZone(zone.id, event)}
											type="button"
										>
											<span
												className={cx(
													"block truncate text-sm font-medium",
													isFocusedZone
														? "text-brand-secondary"
														: "text-primary",
												)}
											>
												{toTitleCase(zone.name)}
											</span>
											<span className="mt-0.5 block">
												<Badge
													color={boundary.color}
													size="sm"
													type="pill-color"
												>
													{boundary.label}
												</Badge>
											</span>
										</button>
									</div>
								</Table.Cell>
								<Table.Cell>
									<span className="text-sm text-secondary tabular-nums">
										{sensors.length}
									</span>
								</Table.Cell>
								<Table.Cell>
									<SensorHealthStatus sensors={sensors} />
								</Table.Cell>
								<Table.Cell>
									<RowActionsMenu
										ariaLabel={`Actions for ${toTitleCase(zone.name)}`}
										items={menuItems}
									/>
								</Table.Cell>
							</Table.Row>
							{isExpanded ? (
								<Table.Row
									className="bg-secondary_subtle hover:bg-secondary_subtle"
									highlightSelectedRow={false}
									id={`${zone.id}-sensors`}
								>
									<Table.Cell
										className="!px-4 !py-3"
										colSpan={ZONE_COLUMN_COUNT}
									>
										<div className="ml-6 md:ml-8" id={panelId}>
											<SensorDetailStrip
												focusSensorId={
													isFocusedZone ? focusSensorId : undefined
												}
												includePagination={includeSensorPagination}
												locationId={locationId}
												onAddSensor={onAddSensor}
												onEditSensor={onEditSensor}
												onViewOnMap={onViewOnMap}
												sensors={sensors}
												zoneId={zone.id}
											/>
										</div>
									</Table.Cell>
								</Table.Row>
							) : null}
						</Fragment>
					);
				})}
			</Table.Body>
		</Table>
	);

	if (pinPagination && showPager) {
		return (
			<div className="flex min-h-0 flex-1 flex-col">
				<div className="min-h-0 flex-1 overflow-auto">{table}</div>
				{pager}
			</div>
		);
	}

	if (pinPagination) {
		return (
			<div className="flex min-h-0 flex-1 flex-col overflow-auto">{table}</div>
		);
	}

	const summarySensorCount = zones.reduce(
		(count, zone) => count + (zone.sensors?.length ?? 0),
		0,
	);

	return (
		<div className="flex flex-col gap-2">
			{showLocationSummary ? (
				<div className="bg-secondary_subtle sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-secondary px-1 py-1.5">
					<p className="text-xs font-medium text-tertiary">
						{toTitleCase(locationName)}
					</p>
					<Badge color="brand" size="sm">
						{zones.length} {zones.length === 1 ? "Zone" : "Zones"}
					</Badge>
					<Badge color="brand" size="sm">
						{summarySensorCount}{" "}
						{summarySensorCount === 1 ? "Sensor" : "Sensors"}
					</Badge>
				</div>
			) : null}
			<div
				className={
					isFramed
						? // Avoid overflow-hidden here: it becomes the sticky containing
							// block and `top-9` on the zone header leaves a blank gap above
							// the column labels. Header cells carry the top radius instead.
							FRAMED_TABLE_SHELL_CLASS
						: undefined
				}
			>
				{table}
				{pager}
			</div>
		</div>
	);
};

const FloorsTable = ({
	expandedFloorIds,
	expandedZoneIds,
	floors,
	focusSensorId,
	focusZoneId,
	hasInstallation,
	includePagination,
	includeSensorPagination,
	locationId,
	locationName,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onEditFloor,
	onEditSensor,
	onEditZone,
	onToggleFloor,
	onToggleZone,
	onViewOnMap,
	stickyTopClassName,
	zones,
}: {
	expandedFloorIds: ReadonlySet<string>;
	expandedZoneIds: ReadonlySet<string>;
	floors: ShadowFloor[];
	focusSensorId?: null | string;
	focusZoneId?: null | string;
	hasInstallation: boolean;
	includePagination?: boolean;
	includeSensorPagination?: boolean;
	locationId: string;
	locationName: string;
	onAddInstallation?: (locationId: string) => void;
	onAddSensor?: (locationId: string, zoneId: string) => void;
	onAddZone?: (locationId: string) => void;
	onEditFloor?: (locationId: string, floorId: string) => void;
	onEditSensor?: (locationId: string, sensorId: string) => void;
	onEditZone?: (locationId: string, zoneId: string) => void;
	onToggleFloor: (floorId: string, event: KeyboardEvent | MouseEvent) => void;
	onToggleZone: (zoneId: string, event: KeyboardEvent | MouseEvent) => void;
	onViewOnMap?: (focus: {
		locationId: string;
		sensorId?: string;
		zoneId?: string;
	}) => void;
	stickyTopClassName: string;
	zones: ShadowZone[];
}) => {
	if (floors.length === 0) return null;

	return (
		<div className="flex flex-col gap-2">
			<div className={FRAMED_TABLE_SHELL_CLASS}>
				<Table
					aria-label={`Floors for ${toTitleCase(locationName)}`}
					className={FRAMED_TABLE_CLASS}
					scrollMode="none"
					size="sm"
				>
					<Table.Header
						sticky={{
							className: cx(stickyTopClassName, "z-20", FRAMED_HEADER_CLASS),
						}}
					>
						<Table.Head className="w-5 px-2" id="floor-expand" />
						<Table.Head
							className="pl-2"
							id="floor-name"
							isRowHeader
							label="Floor"
						/>
						<Table.Head id="floor-zones" label="Zones" />
						<Table.Head id="floor-sensors" label="Sensors" />
						<Table.Head className="w-14" id="floor-actions" />
					</Table.Header>
					<Table.Body>
						{floors.map((floor) => {
							const floorZones = zonesOnFloor(zones, floor.id);
							const floorSensorCount = floorZones.reduce(
								(count, zone) => count + (zone.sensors?.length ?? 0),
								0,
							);
							const isExpanded = expandedFloorIds.has(floor.id);
							const panelId = `floor-zones-${floor.id}`;
							const menuItems = [
								onViewOnMap
									? {
											icon: Map01,
											id: "view-floor-on-map",
											label: "View on map",
											onAction: () => onViewOnMap({ locationId }),
										}
									: null,
								onEditFloor
									? {
											icon: Edit01,
											id: "edit-floor",
											label: "Edit floor",
											onAction: () => onEditFloor(locationId, floor.id),
										}
									: null,
								onAddZone
									? {
											icon: Map02,
											id: "add-zone",
											label: "Add zone",
											onAction: () => onAddZone(locationId),
										}
									: null,
							].filter(Boolean) as RowActionItem[];

							return (
								<Fragment key={floor.id}>
									<Table.Row
										highlightSelectedRow={false}
										id={`list-floor-${floor.id}`}
									>
										<Table.Cell className="w-5 px-2">
											<ExpandToggle
												controlsId={panelId}
												isExpanded={isExpanded}
												label={toTitleCase(floor.name)}
												onToggle={(event) => onToggleFloor(floor.id, event)}
											/>
										</Table.Cell>
										<Table.Cell className="pl-2">
											<button
												aria-controls={panelId}
												aria-expanded={isExpanded}
												className="min-w-0 text-left"
												onClick={(event) => onToggleFloor(floor.id, event)}
												type="button"
											>
												<span className="block truncate text-sm font-medium text-primary">
													{toTitleCase(floor.name)}
												</span>
											</button>
										</Table.Cell>
										<Table.Cell>
											<span className="text-sm text-secondary tabular-nums">
												{floorZones.length}
											</span>
										</Table.Cell>
										<Table.Cell>
											<span className="text-sm text-secondary tabular-nums">
												{floorSensorCount}
											</span>
										</Table.Cell>
										<Table.Cell>
											<RowActionsMenu
												ariaLabel={`Actions for ${toTitleCase(floor.name)}`}
												items={menuItems}
											/>
										</Table.Cell>
									</Table.Row>
									{isExpanded ? (
										<Table.Row
											className="bg-secondary_subtle hover:bg-secondary_subtle"
											highlightSelectedRow={false}
											id={`${floor.id}-zones`}
										>
											<Table.Cell
												className="!px-4 !py-3"
												colSpan={FLOOR_COLUMN_COUNT}
											>
												<div className="ml-6 md:ml-8" id={panelId}>
													<ZonesTable
														expandedZoneIds={expandedZoneIds}
														focusSensorId={focusSensorId}
														focusZoneId={focusZoneId}
														hasInstallation={hasInstallation}
														includePagination={includePagination}
														includeSensorPagination={includeSensorPagination}
														locationId={locationId}
														locationName={floor.name}
														onAddInstallation={onAddInstallation}
														onAddSensor={onAddSensor}
														onAddZone={onAddZone}
														onEditSensor={onEditSensor}
														onEditZone={onEditZone}
														onToggleZone={onToggleZone}
														onViewOnMap={onViewOnMap}
														showLocationSummary={false}
														stickyTopClassName={stickyTopClassName}
														zones={floorZones}
													/>
												</div>
											</Table.Cell>
										</Table.Row>
									) : null}
								</Fragment>
							);
						})}
					</Table.Body>
				</Table>
			</div>
		</div>
	);
};

const LocationNest = ({
	expandedFloorIds,
	expandedZoneIds,
	floors,
	focusSensorId,
	focusZoneId,
	hasInstallation,
	includePagination,
	includeSensorPagination,
	locationId,
	locationName,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onEditFloor,
	onEditSensor,
	onEditZone,
	onToggleFloor,
	onToggleZone,
	onViewOnMap,
	stickyTopClassName,
	zones,
}: {
	expandedFloorIds: ReadonlySet<string>;
	expandedZoneIds: ReadonlySet<string>;
	floors: ShadowFloor[];
	focusSensorId?: null | string;
	focusZoneId?: null | string;
	hasInstallation: boolean;
	includePagination?: boolean;
	includeSensorPagination?: boolean;
	locationId: string;
	locationName: string;
	onAddInstallation?: (locationId: string) => void;
	onAddSensor?: (locationId: string, zoneId: string) => void;
	onAddZone?: (locationId: string) => void;
	onEditFloor?: (locationId: string, floorId: string) => void;
	onEditSensor?: (locationId: string, sensorId: string) => void;
	onEditZone?: (locationId: string, zoneId: string) => void;
	onToggleFloor: (floorId: string, event: KeyboardEvent | MouseEvent) => void;
	onToggleZone: (zoneId: string, event: KeyboardEvent | MouseEvent) => void;
	onViewOnMap?: (focus: {
		locationId: string;
		sensorId?: string;
		zoneId?: string;
	}) => void;
	stickyTopClassName: string;
	zones: ShadowZone[];
}) => {
	const floorIds = new Set(floors.map((floor) => floor.id));
	const unassignedZones = zonesWithoutFloor(zones, floorIds);
	const showUnassignedZones = floors.length === 0 || unassignedZones.length > 0;

	return (
		<div className="flex flex-col gap-3">
			<FloorsTable
				expandedFloorIds={expandedFloorIds}
				expandedZoneIds={expandedZoneIds}
				floors={floors}
				focusSensorId={focusSensorId}
				focusZoneId={focusZoneId}
				hasInstallation={hasInstallation}
				includePagination={includePagination}
				includeSensorPagination={includeSensorPagination}
				locationId={locationId}
				locationName={locationName}
				onAddInstallation={onAddInstallation}
				onAddSensor={onAddSensor}
				onAddZone={onAddZone}
				onEditFloor={onEditFloor}
				onEditSensor={onEditSensor}
				onEditZone={onEditZone}
				onToggleFloor={onToggleFloor}
				onToggleZone={onToggleZone}
				onViewOnMap={onViewOnMap}
				stickyTopClassName={stickyTopClassName}
				zones={zones}
			/>
			{showUnassignedZones ? (
				<ZonesTable
					expandedZoneIds={expandedZoneIds}
					focusSensorId={focusSensorId}
					focusZoneId={focusZoneId}
					framed
					hasInstallation={hasInstallation}
					includePagination={includePagination}
					includeSensorPagination={includeSensorPagination}
					locationId={locationId}
					locationName={locationName}
					onAddInstallation={onAddInstallation}
					onAddSensor={onAddSensor}
					onAddZone={onAddZone}
					onEditSensor={onEditSensor}
					onEditZone={onEditZone}
					onToggleZone={onToggleZone}
					onViewOnMap={onViewOnMap}
					showLocationSummary={false}
					stickyTopClassName={stickyTopClassName}
					zones={floors.length === 0 ? zones : unassignedZones}
				/>
			) : null}
		</div>
	);
};

/** Nested location list: Location → Floors → Zones → Sensors. */
export const LocationListView = ({
	focusFloorId = null,
	focusSensorId = null,
	focusZoneId = null,
	includePagination: includePaginationConfig,
	locationInstallationCounts,
	locations,
	onAddFloor,
	onAddInstallation,
	onAddSensor,
	onAddZone,
	onEditFloor,
	onEditLocation,
	onEditSensor,
	onEditZone,
	onSearchQueryChange,
	onSelectLocation,
	onViewLocation,
	onViewOnMap,
	searchQuery = "",
	selectedLocationId,
	variant = "locations",
}: LocationListViewProps) => {
	const includePagination = resolveListPagination(includePaginationConfig);
	const isZonesVariant = variant === "zones";
	const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [expandedZoneIds, setExpandedZoneIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [expandedFloorIds, setExpandedFloorIds] = useState<Set<string>>(
		() => new Set(),
	);
	const [locationPage, setLocationPage] = useState(1);
	const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
		column: "name",
		direction: "ascending",
	});

	const trimmedSearch = searchQuery.trim().toLowerCase();
	const focusedLocation = isZonesVariant ? (locations[0] ?? null) : null;
	const resolvedFocus = useMemo(
		() =>
			resolveLocationMapFocus(
				{
					floorId: focusFloorId,
					sensorId: focusSensorId,
					zoneId: focusZoneId,
				},
				toLocationMapFocusEntities(locations),
			),
		[focusFloorId, focusSensorId, focusZoneId, locations],
	);
	const resolvedFocusZoneId = resolvedFocus.zoneId;
	const resolvedFocusSensorId = resolvedFocus.sensorId;
	const resolvedFocusFloorId = resolvedFocus.floorId;

	const focusLocationId = useMemo(() => {
		if (resolvedFocusZoneId) {
			return (
				locations.find((location) =>
					(location.zones ?? []).some(
						(zone) => zone.id === resolvedFocusZoneId,
					),
				)?.id ?? null
			);
		}
		if (!resolvedFocusFloorId) return null;
		return (
			locations.find((location) =>
				(location.floors ?? []).some(
					(floor) => floor.id === resolvedFocusFloorId,
				),
			)?.id ?? null
		);
	}, [locations, resolvedFocusFloorId, resolvedFocusZoneId]);

	// Expand the URL-focused floor, zone, and location when focus params change.
	const focusKey = `${focusLocationId ?? ""}:${resolvedFocusFloorId ?? ""}:${resolvedFocusZoneId ?? ""}:${resolvedFocusSensorId ?? ""}`;
	const [prevFocusKey, setPrevFocusKey] = useState<null | string>(null);
	if (focusKey !== prevFocusKey) {
		setPrevFocusKey(focusKey);
		if (resolvedFocusZoneId) {
			setExpandedZoneIds((current) => {
				if (current.has(resolvedFocusZoneId)) return current;
				const next = new Set(current);
				next.add(resolvedFocusZoneId);
				return next;
			});
		}
		if (resolvedFocusFloorId) {
			setExpandedFloorIds((current) => {
				if (current.has(resolvedFocusFloorId)) return current;
				const next = new Set(current);
				next.add(resolvedFocusFloorId);
				return next;
			});
		}
		if (focusLocationId && !isZonesVariant) {
			setExpandedLocationIds((current) => {
				if (current.has(focusLocationId)) return current;
				const next = new Set(current);
				next.add(focusLocationId);
				return next;
			});
		}
	}

	const filteredLocations = useMemo(() => {
		if (!trimmedSearch) return locations;
		return locations.filter((location) => {
			if (location.name.toLowerCase().includes(trimmedSearch)) return true;
			return (
				(location.zones ?? []).some(
					(zone) =>
						zone.name.toLowerCase().includes(trimmedSearch) ||
						(zone.sensors ?? []).some((sensor) =>
							sensor.name.toLowerCase().includes(trimmedSearch),
						),
				) ||
				(location.floors ?? []).some((floor) =>
					floor.name.toLowerCase().includes(trimmedSearch),
				)
			);
		});
	}, [locations, trimmedSearch]);

	const filteredZones = useMemo(() => {
		if (!focusedLocation) return [];
		const zones = focusedLocation.zones ?? [];
		if (!trimmedSearch) return zones;
		return zones.filter(
			(zone) =>
				zone.name.toLowerCase().includes(trimmedSearch) ||
				(zone.sensors ?? []).some((sensor) =>
					sensor.name.toLowerCase().includes(trimmedSearch),
				),
		);
	}, [focusedLocation, trimmedSearch]);

	const sortedLocations = useMemo(() => {
		const column = String(sortDescriptor.column ?? "name");
		return [...filteredLocations].sort((a, b) => {
			switch (column) {
				case "floors":
					return compareSortValues(
						locationFloorCount(a),
						locationFloorCount(b),
						sortDescriptor.direction,
					);
				case "installation":
					return compareSortValues(
						locationInstallationCounts?.get(a.id) ?? 0,
						locationInstallationCounts?.get(b.id) ?? 0,
						sortDescriptor.direction,
					);
				case "sensors":
					return compareSortValues(
						locationSensorCount(a),
						locationSensorCount(b),
						sortDescriptor.direction,
					);
				case "status":
					return compareSortValues(
						sensorHealthSortKey(locationSensors(a)),
						sensorHealthSortKey(locationSensors(b)),
						sortDescriptor.direction,
					);
				case "zones":
					return compareSortValues(
						a.zones?.length ?? 0,
						b.zones?.length ?? 0,
						sortDescriptor.direction,
					);
				case "name":
				default:
					return compareSortValues(
						a.name ?? "",
						b.name ?? "",
						sortDescriptor.direction,
					);
			}
		});
	}, [filteredLocations, locationInstallationCounts, sortDescriptor]);

	// When search changes, open ancestor paths for nested matches (adjust state during render).
	const [prevTrimmedSearch, setPrevTrimmedSearch] = useState(trimmedSearch);
	if (trimmedSearch !== prevTrimmedSearch) {
		setPrevTrimmedSearch(trimmedSearch);
		setLocationPage(1);
		if (trimmedSearch) {
			if (isZonesVariant && focusedLocation) {
				const nextFloors = new Set<string>();
				for (const floor of sortFloorsForList(focusedLocation.floors ?? [])) {
					const floorMatch = floor.name.toLowerCase().includes(trimmedSearch);
					const zoneMatch = filteredZones.some(
						(zone) => getRefId(zone.floor) === floor.id,
					);
					if (floorMatch || zoneMatch) nextFloors.add(floor.id);
				}
				setExpandedFloorIds(nextFloors);
				setExpandedZoneIds(new Set(filteredZones.map((zone) => zone.id)));
			} else {
				const nextLocations = new Set<string>();
				const nextZones = new Set<string>();
				const nextFloors = new Set<string>();
				for (const location of filteredLocations) {
					const locationMatch = location.name
						.toLowerCase()
						.includes(trimmedSearch);
					let locationNeedsOpen = locationMatch;
					for (const floor of location.floors ?? []) {
						if (floor.name.toLowerCase().includes(trimmedSearch)) {
							locationNeedsOpen = true;
							nextFloors.add(floor.id);
						}
					}
					for (const zone of location.zones ?? []) {
						const zoneMatch = zone.name.toLowerCase().includes(trimmedSearch);
						const sensorMatch = (zone.sensors ?? []).some((sensor) =>
							sensor.name.toLowerCase().includes(trimmedSearch),
						);
						if (zoneMatch || sensorMatch) {
							locationNeedsOpen = true;
							nextZones.add(zone.id);
							const floorId = getRefId(zone.floor);
							if (floorId) nextFloors.add(floorId);
						}
					}
					if (locationNeedsOpen) nextLocations.add(location.id);
				}
				setExpandedLocationIds(nextLocations);
				setExpandedZoneIds(nextZones);
				setExpandedFloorIds(nextFloors);
			}
		}
	}

	const focusedLocationIndex = focusLocationId
		? sortedLocations.findIndex((location) => location.id === focusLocationId)
		: -1;
	const [prevFocusLocationId, setPrevFocusLocationId] = useState<
		null | string | undefined
	>(undefined);
	if (focusLocationId !== prevFocusLocationId) {
		setPrevFocusLocationId(focusLocationId);
		if (
			includePagination.locations &&
			!isZonesVariant &&
			focusedLocationIndex >= 0
		) {
			setLocationPage(Math.floor(focusedLocationIndex / LIST_PAGE_SIZE) + 1);
		}
	}

	const locationPagination = useMemo(
		() => paginate(sortedLocations, locationPage),
		[locationPage, sortedLocations],
	);
	const visibleLocations = includePagination.locations
		? locationPagination.pageItems
		: sortedLocations;

	const toggleLocation = (
		locationId: string,
		event: KeyboardEvent | MouseEvent,
	) => {
		event.stopPropagation();
		setExpandedLocationIds((current) => {
			const next = new Set(current);
			if (next.has(locationId)) next.delete(locationId);
			else next.add(locationId);
			return next;
		});
	};

	const toggleZone = (zoneId: string, event: KeyboardEvent | MouseEvent) => {
		event.stopPropagation();
		setExpandedZoneIds((current) => {
			const next = new Set(current);
			if (next.has(zoneId)) next.delete(zoneId);
			else next.add(zoneId);
			return next;
		});
	};

	const toggleFloor = (floorId: string, event: KeyboardEvent | MouseEvent) => {
		event.stopPropagation();
		setExpandedFloorIds((current) => {
			const next = new Set(current);
			if (next.has(floorId)) next.delete(floorId);
			else next.add(floorId);
			return next;
		});
	};

	const visibleLocationIds = sortedLocations.map((location) => location.id);
	const visibleZoneIds = isZonesVariant
		? filteredZones.map((zone) => zone.id)
		: sortedLocations.flatMap((location) =>
				(location.zones ?? []).map((zone) => zone.id),
			);
	const visibleFloorIds = isZonesVariant
		? sortFloorsForList(focusedLocation?.floors ?? []).map((floor) => floor.id)
		: sortedLocations.flatMap((location) =>
				sortFloorsForList(location.floors ?? []).map((floor) => floor.id),
			);
	const canExpandAll = isZonesVariant
		? (visibleZoneIds.length > 0 &&
				visibleZoneIds.some((id) => !expandedZoneIds.has(id))) ||
			(visibleFloorIds.length > 0 &&
				visibleFloorIds.some((id) => !expandedFloorIds.has(id)))
		: sortedLocations.length > 0 &&
			(visibleLocationIds.some((id) => !expandedLocationIds.has(id)) ||
				visibleZoneIds.some((id) => !expandedZoneIds.has(id)) ||
				visibleFloorIds.some((id) => !expandedFloorIds.has(id)));
	const canCollapseAll = isZonesVariant
		? visibleZoneIds.some((id) => expandedZoneIds.has(id)) ||
			visibleFloorIds.some((id) => expandedFloorIds.has(id))
		: visibleLocationIds.some((id) => expandedLocationIds.has(id)) ||
			visibleZoneIds.some((id) => expandedZoneIds.has(id)) ||
			visibleFloorIds.some((id) => expandedFloorIds.has(id));

	const expandAll = () => {
		if (!isZonesVariant) {
			setExpandedLocationIds(new Set(visibleLocationIds));
		}
		setExpandedZoneIds(new Set(visibleZoneIds));
		setExpandedFloorIds(new Set(visibleFloorIds));
	};

	const collapseAll = () => {
		if (!isZonesVariant) {
			setExpandedLocationIds(new Set());
		}
		setExpandedZoneIds(new Set());
		setExpandedFloorIds(new Set());
	};

	const hasSearchFilter = Boolean(trimmedSearch);

	const listToolbar = (
		<div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-secondary bg-primary p-4">
			<div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
				{onSearchQueryChange ? (
					<SearchInputWithClear
						aria-label={
							isZonesVariant
								? "Search floors, zones, or sensors"
								: "Search locations"
						}
						className="w-full max-w-sm"
						onChange={onSearchQueryChange}
						placeholder={
							isZonesVariant
								? "Search floors, zones, or sensors..."
								: "Search locations, floors, zones, or sensors..."
						}
						size="sm"
						value={searchQuery}
					/>
				) : null}
			</div>
			<div className="flex items-center gap-2">
				<Button
					color="secondary"
					isDisabled={!canExpandAll}
					onClick={expandAll}
					size="sm"
				>
					Expand all
				</Button>
				<Button
					color="secondary"
					isDisabled={!canCollapseAll}
					onClick={collapseAll}
					size="sm"
				>
					Collapse all
				</Button>
			</div>
		</div>
	);

	if (isZonesVariant) {
		if (!focusedLocation) {
			return (
				<TableCard.Root className="flex min-h-0 flex-1 flex-col overflow-hidden shadow-none">
					<p className="px-4 py-10 text-center text-sm text-tertiary md:px-6">
						No location to display.
					</p>
				</TableCard.Root>
			);
		}

		const hasInstallation =
			(locationInstallationCounts?.get(focusedLocation.id) ?? 0) > 0;

		return (
			<TableCard.Root className="flex min-h-0 flex-1 flex-col overflow-hidden shadow-none">
				{listToolbar}

				{hasSearchFilter &&
				filteredZones.length === 0 &&
				!sortFloorsForList(focusedLocation.floors ?? []).some((floor) =>
					floor.name.toLowerCase().includes(trimmedSearch),
				) ? (
					<p className="px-4 py-10 text-center text-sm text-tertiary md:px-6">
						No floors, zones, or sensors match “{searchQuery.trim()}”.
					</p>
				) : (
					<div className="min-h-0 flex-1 overflow-auto px-4 py-4 md:px-6">
						<LocationNest
							expandedFloorIds={expandedFloorIds}
							expandedZoneIds={expandedZoneIds}
							floors={sortFloorsForList(
								(focusedLocation.floors ?? []).filter((floor) => {
									if (!trimmedSearch) return true;
									if (floor.name.toLowerCase().includes(trimmedSearch)) {
										return true;
									}
									return filteredZones.some(
										(zone) => getRefId(zone.floor) === floor.id,
									);
								}),
							)}
							focusSensorId={resolvedFocusSensorId}
							focusZoneId={resolvedFocusZoneId}
							hasInstallation={hasInstallation}
							includePagination={includePagination.zones}
							includeSensorPagination={includePagination.sensors}
							locationId={focusedLocation.id}
							locationName={focusedLocation.name}
							onAddInstallation={onAddInstallation}
							onAddSensor={onAddSensor}
							onAddZone={onAddZone}
							onEditFloor={onEditFloor}
							onEditSensor={onEditSensor}
							onEditZone={onEditZone}
							onToggleFloor={toggleFloor}
							onToggleZone={toggleZone}
							onViewOnMap={onViewOnMap}
							stickyTopClassName={LOCATION_HEADER_STICKY_TOP}
							zones={
								trimmedSearch
									? (focusedLocation.zones ?? []).filter((zone) => {
											const floorName = sortFloorsForList(
												focusedLocation.floors ?? [],
											)
												.find((floor) => floor.id === getRefId(zone.floor))
												?.name.toLowerCase();
											if (floorName?.includes(trimmedSearch)) return true;
											return (
												zone.name.toLowerCase().includes(trimmedSearch) ||
												(zone.sensors ?? []).some((sensor) =>
													sensor.name.toLowerCase().includes(trimmedSearch),
												)
											);
										})
									: (focusedLocation.zones ?? [])
							}
						/>
					</div>
				)}
			</TableCard.Root>
		);
	}

	return (
		<TableCard.Root className="flex min-h-0 flex-1 flex-col overflow-hidden shadow-none">
			{listToolbar}

			{filteredLocations.length === 0 ? (
				<p className="px-4 py-10 text-center text-sm text-tertiary md:px-6">
					{hasSearchFilter
						? `No locations match “${searchQuery.trim()}”.`
						: "No locations yet."}
				</p>
			) : (
				<>
					<div className="min-h-0 flex-1 overflow-auto">
						<Table
							aria-label="Locations"
							// className="min-w-[44rem]"
							onSortChange={setSortDescriptor}
							scrollMode="none"
							size="sm"
							sortDescriptor={sortDescriptor}
						>
							<Table.Header
								sticky={{ className: cx(LOCATION_HEADER_STICKY_TOP, "z-30") }}
							>
								<Table.Head className="w-5 px-2" id="location-expand" />
								<Table.Head
									allowsSorting
									className="pl-2"
									id="name"
									isRowHeader
									label="Name"
								/>
								<Table.Head allowsSorting id="floors" label="Floors" />
								<Table.Head allowsSorting id="zones" label="Zones" />
								<Table.Head allowsSorting id="sensors" label="Sensors" />
								<Table.Head
									allowsSorting
									id="installation"
									label="Installations"
								/>
								<Table.Head allowsSorting id="status" label="Status" />
								<Table.Head className="w-14" id="location-actions" />
							</Table.Header>

							<Table.Body>
								{visibleLocations.map((location) => {
									const zones = location.zones ?? [];
									const isExpanded = expandedLocationIds.has(location.id);
									const panelId = `location-zones-${location.id}`;
									const isSelected = selectedLocationId === location.id;
									const installationCount =
										locationInstallationCounts?.get(location.id) ?? 0;
									const hasInstallation = installationCount > 0;
									const sensors = locationSensors(location);
									const missingSetup = getLocationSetupGaps(
										location,
										hasInstallation,
									);
									const locationMappable = isLocationMappable(location);
									const menuItems = [
										onViewLocation
											? {
													icon: Eye,
													id: "view-location",
													label: "View location",
													onAction: () => onViewLocation(location.id),
												}
											: null,
										onViewOnMap
											? {
													description: locationMappable
														? undefined
														: VIEW_ON_MAP_DISABLED_DESCRIPTION,
													icon: Map01,
													id: "view-location-on-map",
													isDisabled: !locationMappable,
													label: "View on map",
													onAction: () =>
														onViewOnMap({ locationId: location.id }),
												}
											: null,
										onEditLocation
											? {
													icon: Edit01,
													id: "edit-location",
													label: "Edit location",
													onAction: () => onEditLocation(location.id),
												}
											: null,
										onAddInstallation
											? {
													icon: Save01,
													id: "add-installation",
													label: "Add installation",
													onAction: () => onAddInstallation(location.id),
												}
											: null,
										onAddFloor
											? {
													description: hasInstallation
														? undefined
														: "Create an installation for this location first",
													icon: LayersTwo01,
													id: "add-floor",
													isDisabled: !hasInstallation,
													label: "Add floor",
													onAction: () => onAddFloor(location.id),
												}
											: null,
										onAddZone
											? {
													description: hasInstallation
														? undefined
														: "Create an installation for this location first",
													icon: Map02,
													id: "add-zone",
													isDisabled: !hasInstallation,
													label: "Add zone",
													onAction: () => onAddZone(location.id),
												}
											: null,
									].filter(Boolean) as RowActionItem[];

									const openLocation = onViewLocation ?? onSelectLocation;

									return (
										<Fragment key={location.id}>
											<Table.Row
												className={cx(
													"cursor-pointer",
													isSelected && "bg-brand-primary_alt",
												)}
												highlightSelectedRow={false}
												id={location.id}
												onAction={() => {
													setExpandedLocationIds((current) => {
														const next = new Set(current);
														if (next.has(location.id)) next.delete(location.id);
														else next.add(location.id);
														return next;
													});
												}}
											>
												<Table.Cell className="w-5 px-2">
													<ExpandToggle
														controlsId={panelId}
														isExpanded={isExpanded}
														label={toTitleCase(location.name)}
														onToggle={(event) =>
															toggleLocation(location.id, event)
														}
													/>
												</Table.Cell>
												<Table.Cell className="pl-2">
													<div className="min-w-0 text-left">
														{openLocation ? (
															<button
																className="text-sm font-medium text-brand-secondary underline-offset-2 hover:text-brand-secondary_hover hover:underline"
																onClick={(event) => {
																	event.stopPropagation();
																	openLocation(location.id);
																}}
																type="button"
															>
																{toTitleCase(location.name)}
															</button>
														) : (
															<p className="text-sm font-medium text-primary">
																{toTitleCase(location.name)}
															</p>
														)}
														<p className="text-sm text-tertiary">
															{formatLocationAddress(location)}
														</p>
													</div>
												</Table.Cell>
												<Table.Cell>
													<span className="text-sm text-secondary tabular-nums">
														{locationFloorCount(location)}
													</span>
												</Table.Cell>
												<Table.Cell>
													<span className="text-sm text-secondary tabular-nums">
														{zones.length}
													</span>
												</Table.Cell>
												<Table.Cell>
													<span className="text-sm text-secondary tabular-nums">
														{locationSensorCount(location)}
													</span>
												</Table.Cell>
												<Table.Cell>
													<span className="text-sm text-secondary tabular-nums">
														{installationCount}
													</span>
												</Table.Cell>
												<Table.Cell>
													<SensorHealthStatus
														missingSetup={missingSetup}
														sensors={sensors}
													/>
												</Table.Cell>
												<Table.Cell>
													<RowActionsMenu
														ariaLabel={`Actions for ${toTitleCase(location.name)}`}
														items={menuItems}
													/>
												</Table.Cell>
											</Table.Row>

											{isExpanded ? (
												<Table.Row
													className="bg-secondary_subtle hover:bg-secondary_subtle"
													highlightSelectedRow={false}
													id={`${location.id}-zones`}
												>
													<Table.Cell
														className="!px-4 !py-3"
														colSpan={LOCATION_COLUMN_COUNT}
													>
														<div className="ml-6 md:ml-8" id={panelId}>
															<LocationNest
																expandedFloorIds={expandedFloorIds}
																expandedZoneIds={expandedZoneIds}
																floors={sortFloorsForList(
																	location.floors ?? [],
																)}
																focusSensorId={resolvedFocusSensorId}
																focusZoneId={resolvedFocusZoneId}
																hasInstallation={hasInstallation}
																includePagination={includePagination.zones}
																includeSensorPagination={
																	includePagination.sensors
																}
																locationId={location.id}
																locationName={location.name}
																onAddInstallation={onAddInstallation}
																onAddSensor={onAddSensor}
																onAddZone={onAddZone}
																onEditFloor={onEditFloor}
																onEditSensor={onEditSensor}
																onEditZone={onEditZone}
																onToggleFloor={toggleFloor}
																onToggleZone={toggleZone}
																onViewOnMap={onViewOnMap}
																stickyTopClassName={ZONE_HEADER_STICKY_TOP}
																zones={zones}
															/>
														</div>
													</Table.Cell>
												</Table.Row>
											) : null}
										</Fragment>
									);
								})}
							</Table.Body>
						</Table>
					</div>
					{includePagination.locations ? (
						<ListPagination
							className="px-4 md:px-6"
							onPageChange={setLocationPage}
							page={locationPagination.page}
							total={locationPagination.total}
							totalPages={locationPagination.totalPages}
						/>
					) : null}
				</>
			)}
		</TableCard.Root>
	);
};
